import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Grid, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import first from 'lodash/first';
import {
  SidebarDrawer,
  GoogleAddressField,
  AddressType as Address,
} from '@pv/common/components';
import {
  useCreatePlannerMutation,
  useUpdatePlannerMutation,
} from '@pv/common/graphql';
import { AccountAutocomplete } from './AccountAutocomplete';
import validator from 'validator';
import { useVenues } from '../providers/venues';
import { PlannerEventStats } from './PlannerEventStats';
import { PlannerUpdateWarningModal } from './PlannerUpdateWarningModal';
import { gql, useQuery } from '@apollo/client';
import {
  PlannerQuery,
  PlannerQueryVariables,
  UpdatePlannerMutation,
  CreatePlannerMutation,
} from '@pv/common/graphql/operations';
import { CreatePlannerInput } from '@pv/common/graphql/types';
import { eventContactsQuery } from '../hooks/useEventContactsQuery';
import {
  EventContactsQuery,
  EventContactsQueryVariables,
} from '@pv/common/graphql/operations';

import { PhoneField } from './PhoneField';

const plannerQuery = gql`
  query PlannerQuery($id: ID) {
    planner(id: $id) {
      id
      firstName
      lastName
      email
      phone
      guest {
        id
        gravatarUrl
      }
      account {
        id
        name
      }
      notes
      title
      eventsCount
      address {
        id
        streetAddress1
        streetAddress2
        city
        state
        zip
        country
        formattedAddress
      }
    }
  }
`;

interface PlannerDrawerProps {
  plannerId?: string;
  open: boolean;
  onClose: () => void;
  afterMutate?: () => void;
  readOnly?: boolean;
  prefilledFirstName?: string;
  eventId?: string;
  onDelete?: () => void;
}

export const PlannerDrawer = ({
  plannerId,
  open,
  onClose,
  afterMutate,
  readOnly = false,
  prefilledFirstName = '',
  eventId,
  onDelete = undefined,
}: PlannerDrawerProps) => {
  const { venueIds } = useVenues();
  const venueId = first(venueIds);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [firstName, setFirstName] = useState(prefilledFirstName);
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accountName, setAccountName] = useState('');
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState<Address>({
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    formattedAddress: '',
  });
  const { data } = useQuery<PlannerQuery, PlannerQueryVariables>(plannerQuery, {
    variables: {
      id: plannerId,
    },
    skip: !plannerId || !open,
  });

  const plannerData = data?.planner;
  const totalEventCount = plannerData?.eventsCount ?? 0;

  useEffect(() => {
    const setStateForPlanner = () => {
      setFirstName(plannerData?.firstName || prefilledFirstName);
      setLastName(plannerData?.lastName || '');
      setEmail(plannerData?.email || '');
      setPhone(plannerData?.phone || '');
      setAccountName(plannerData?.account?.name || '');
      setNotes(plannerData?.notes || '');
      setTitle(plannerData?.title || '');
      setAddress({
        streetAddress1: plannerData?.address?.streetAddress1 || '',
        streetAddress2: plannerData?.address?.streetAddress2 || '',
        city: plannerData?.address?.city || '',
        state: plannerData?.address?.state || '',
        zip: plannerData?.address?.zip || '',
        country: plannerData?.address?.country || '',
        formattedAddress: plannerData?.address?.formattedAddress || '',
      });
      setShowErrors(false);
    };
    setStateForPlanner();
  }, [data, open, plannerData, prefilledFirstName]);

  const [createPlanner, { loading: createLoading }] = useCreatePlannerMutation({
    onCompleted: (data: CreatePlannerMutation) => {
      if (data?.createPlanner?.errors?.length) {
        data.createPlanner.errors.forEach((error) => {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
        });
        setShowErrors(true);
      } else {
        onClose();
        enqueueSnackbar('Created Contact', { variant: 'success' });
        if (afterMutate) {
          afterMutate();
        }
      }
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
    update: (cache, { data }) => {
      if (!data || !data.createPlanner) {
        return;
      }

      if (eventId) {
        const existingContacts = cache.readQuery<
          EventContactsQuery,
          EventContactsQueryVariables
        >({
          query: eventContactsQuery,
          variables: { eventId },
        });

        if (existingContacts?.event?.additionalContacts) {
          cache.writeQuery({
            query: eventContactsQuery,
            variables: { eventId },
            data: {
              event: {
                ...existingContacts.event,
                additionalContacts: [
                  ...existingContacts.event.additionalContacts,
                  data.createPlanner.planner,
                ],
              },
            },
          });
        }
      }
    },
  });

  const [updatePlanner, { loading: updateLoading }] = useUpdatePlannerMutation({
    onCompleted: (data: UpdatePlannerMutation) => {
      if (data?.updatePlanner?.errors?.length) {
        data.updatePlanner.errors.forEach((error) => {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
        });
        setShowErrors(true);
      } else {
        onClose();
        enqueueSnackbar('Updated Contact', { variant: 'success' });
        if (afterMutate) {
          afterMutate();
        }
      }
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const executeSave = () => {
    if (plannerId) {
      const input = {
        firstName,
        lastName,
        email,
        phone,
        notes,
        accountName,
        title,
        address: {
          streetAddress1: address.streetAddress1,
          streetAddress2: address.streetAddress2,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country,
        },
        id: plannerId,
      };
      updatePlanner({ variables: { input } });
    } else if (venueId) {
      const input: CreatePlannerInput = {
        firstName,
        lastName,
        email,
        phone,
        notes,
        accountName,
        venueId,
        title,
        address: {
          streetAddress1: address.streetAddress1,
          streetAddress2: address.streetAddress2,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country,
        },
      };
      if (eventId) {
        input.eventId = eventId;
      }
      createPlanner({ variables: { input } });
    }
  };

  const onSave = () => {
    if (plannerId && totalEventCount > 1) {
      setConfirmDialogOpen(true);
    } else {
      executeSave();
    }
  };

  const handleConfirmSave = () => {
    setConfirmDialogOpen(false);
    executeSave();
  };

  const loading = createLoading || updateLoading;
  const plannerInfoControlledByGuest = !!plannerData?.guest?.id;
  return (
    <SidebarDrawer
      open={open}
      onClose={onClose}
      title={plannerId ? 'Edit Contact' : 'New Contact'}
      onSave={onSave}
      loading={loading}
    >
      <PlannerUpdateWarningModal
        isOpen={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        totalEventCount={totalEventCount}
        handleConfirmUpdate={handleConfirmSave}
      />
      <Grid container spacing={2} data-cy="event-drawer-contact-edit">
        {plannerId && totalEventCount > 1 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              This contact is linked to {totalEventCount} events. Edits will
              update their info across all of them.
            </Alert>
          </Grid>
        )}
        <Grid item xs={6}>
          <TextField
            fullWidth
            required
            value={firstName}
            name="firstName"
            onChange={(e) => setFirstName(e.target.value)}
            label="First Name"
            disabled={readOnly || plannerInfoControlledByGuest}
            autoFocus={!!prefilledFirstName}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            value={lastName}
            name="lastName"
            onChange={(e) => setLastName(e.target.value)}
            label="Last Name"
            disabled={readOnly || plannerInfoControlledByGuest}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="title"
            label="Title"
            disabled={readOnly}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <AccountAutocomplete
            value={accountName}
            name="account"
            onChange={setAccountName}
            label="Account"
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="email"
            value={email}
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            label="Email"
            disabled={readOnly || plannerInfoControlledByGuest}
            error={showErrors && !!email && !validator.isEmail(email)}
            helperText={
              showErrors && !!email && !validator.isEmail(email)
                ? 'Invalid Email'
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12}>
          <PhoneField
            fullWidth
            value={phone}
            name="phone"
            onChange={(e) => setPhone(e.target.value)}
            label="Phone"
            type="tel"
            disabled={readOnly || plannerInfoControlledByGuest}
            error={showErrors && !!phone && !validator.isMobilePhone(phone)}
            helperText={
              showErrors && !!phone && !validator.isMobilePhone(phone)
                ? 'Invalid Phone Number'
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <GoogleAddressField
            value={address}
            onChange={(address) => setAddress(address)}
            disabled={readOnly || plannerInfoControlledByGuest}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <TextField
            inputProps={{ spellCheck: true }}
            multiline
            fullWidth
            minRows={6}
            value={notes}
            name="notes"
            onChange={(e) => setNotes(e.target.value)}
            label="Notes"
            disabled={readOnly}
          />
        </Grid>
        {plannerId && (
          <Grid item xs={12}>
            <PlannerEventStats plannerId={plannerId} />
          </Grid>
        )}
        {onDelete && (
          <Grid item xs={12}>
            <Button variant="outlined" color="secondary" onClick={onDelete}>
              Delete
            </Button>
          </Grid>
        )}
      </Grid>
    </SidebarDrawer>
  );
};

export default PlannerDrawer;
